import { useState } from "react";

import { useAuth } from "@/contexts/auth/useAuth";

import { useDebounce } from "./useDebounce";

import type Settings from "@/settings";

const QUERY_DEBOUNCE_MS = 300;
const MAX_RECENT_SEARCHES = 10;

interface UseRecentSearchesOptions {
  /** Seeds the input, e.g. from a `?q=` param. */
  initialValue?: string;
}

interface UseRecentSearches {
  value: string;
  setValue(value: string): void;
  /** `value`, 300ms after the last keystroke — what to query with. */
  debouncedValue: string;
  recentSearches: string[];
  /** Re-reads the store, which another mounted search may have written to. */
  refreshRecentSearches(): void;
  commitSearch(term: string): void;
  clearRecentSearch(term: string): void;
  clearRecentSearches(): void;
}

/** Search query state whose committed terms persist to localStorage per account. */
function useRecentSearches(
  settings: Settings,
  { initialValue = "" }: UseRecentSearchesOptions = {},
): UseRecentSearches {
  const { currentAccount } = useAuth();
  const id = currentAccount?.id ?? "";

  const readStoredSearches = (): string[] => settings.get(id) || [];

  const [recentSearches, setRecentSearches] =
    useState<string[]>(readStoredSearches);

  const [value, setValue] = useState(initialValue);
  const debouncedValue = useDebounce(value, QUERY_DEBOUNCE_MS);

  const persist = (terms: string[]) => {
    setRecentSearches(terms);
    settings.set(id, terms);
  };

  const refreshRecentSearches = () => setRecentSearches(readStoredSearches());

  const commitSearch = (term: string) =>
    persist(
      [term, ...recentSearches.filter((existing) => existing !== term)].slice(
        0,
        MAX_RECENT_SEARCHES,
      ),
    );

  const clearRecentSearch = (term: string) =>
    persist(recentSearches.filter((existing) => existing !== term));

  const clearRecentSearches = () => {
    setRecentSearches([]);
    settings.remove(id);
  };

  return {
    value,
    setValue,
    debouncedValue,
    recentSearches,
    refreshRecentSearches,
    commitSearch,
    clearRecentSearch,
    clearRecentSearches,
  };
}

export { useRecentSearches };
